import { useState } from "react";
import {
  Box,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Progress,
  Badge,
  Center,
  Loader,
  SimpleGrid,
  Table,
  Button,
  ScrollArea,
  Tooltip,
  ThemeIcon,
} from "@mantine/core";
import {
  IconFilter,
  IconBrandWhatsapp,
  IconPhone,
  IconChevronDown,
  IconChevronRight,
  IconUserCheck,
  IconUserX,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";

type Stage = {
  key: string;
  label: string;
  count: number;
  pctOfTotal: number;
  pctOfPrev: number;
  dropFromPrev: number;
};
type NotMonetized = {
  total: number;
  stages: {
    neverOpened: number;
    openedNoTutorial: number;
    midTutorial: number;
    finishedNeedsSocialOrProfile: number;
  };
  gates: { needProfile: number; needTutorials: number; needSocial: number };
  warmth: { openedThisWeek: number; opened7to30d: number; neverOpened: number };
};
type FunnelData = {
  funnel: Stage[];
  setup: { verifiedPhone: number; setPin: number; addedBank: number };
  notMonetized?: NotMonetized;
  totalUsers: number;
  generatedAt: string;
};
type CohortUser = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  phoneVerified: boolean;
  monetized: boolean;
  balance: number;
  joinedAt: string;
  lastLoginAt: string | null;
};
type CohortData = { total: number; shown: number; users: CohortUser[] };
type PanelState = { loading: boolean; data: CohortData | null };

// "3d ago", "5w ago" — how long since a date (for spotting the truly stuck).
function ago(dateStr: string | null): string {
  if (!dateStr) return "—";
  const ms = Date.now() - new Date(dateStr).getTime();
  if (ms < 0 || Number.isNaN(ms)) return "—";
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// Nigerian number → wa.me international form (strip +/leading 0, ensure 234).
function waLink(phone: string | null): string | null {
  if (!phone) return null;
  let d = phone.replace(/\D/g, "");
  if (d.startsWith("0")) d = "234" + d.slice(1);
  else if (!d.startsWith("234") && d.length === 10) d = "234" + d;
  if (d.length < 11) return null;
  return `https://wa.me/${d}`;
}

// Plain-English "who are these people" for each SURVIVOR stage (green bars).
const STAGE_DESC: Record<string, string> = {
  signed_up: "Everyone who has ever created an account.",
  monetized: "Finished the tutorials — they can now earn from opportunities.",
  withdrew: "Have cashed out to their bank at least once.",
  active_7d: "Did something in the app in the last 7 days.",
};

// The DROP-OFF group that sits above each stage (a DISTINCT set of people who
// stalled — they do NOT overlap with the survivors below).
const DROP_KEY = ["", "not_monetized", "monetized_no_withdrawal", "dormant"];
const DROP_TITLE: Record<string, string> = {
  not_monetized: "Signed up but never finished the tutorials",
  monetized_no_withdrawal: "Earning but have never withdrawn",
  dormant: "Earned before, now gone quiet",
};
const DROP_DESC: Record<string, string> = {
  not_monetized:
    "They created an account but never completed the tutorials, so they can't earn yet. This is usually the biggest leak.",
  monetized_no_withdrawal:
    "They finished tutorials and have money in their wallet, but have never cashed out to their bank.",
  dormant:
    "They earned before but have had no activity for 7+ days. Worth re-engaging.",
};

const SETUP_TITLE: Record<string, string> = {
  no_phone: "Members without a verified phone",
  no_pin: "Members without a transaction PIN",
  no_bank: "Members with no bank added",
};

export default function Funnel() {
  const { data, isLoading } = usePerformRequest<{ data: FunnelData }>({
    method: Endpoints.GetFunnel().method,
    route: Endpoints.GetFunnel().route,
  });
  const d = data?.data;

  // Each cohort key can be independently expanded inline. Lazy-loaded on open.
  const [panels, setPanels] = useState<Record<string, PanelState>>({});

  const toggle = async (stage: string) => {
    if (panels[stage]) {
      setPanels((p) => {
        const { [stage]: _drop, ...rest } = p;
        return rest;
      });
      return;
    }
    setPanels((p) => ({ ...p, [stage]: { loading: true, data: null } }));
    try {
      const ep = Endpoints.GetFunnelCohort(stage);
      const res = await PerformRequest<any>({
        method: ep.method,
        route: ep.route,
      });
      const payload = (res.data as any)?.data ?? res.data;
      setPanels((p) => ({
        ...p,
        [stage]: {
          loading: false,
          data: payload || { total: 0, shown: 0, users: [] },
        },
      }));
    } catch {
      setPanels((p) => ({
        ...p,
        [stage]: { loading: false, data: { total: 0, shown: 0, users: [] } },
      }));
    }
  };

  return (
    <Box p="md">
      <Group mb="xs" gap="xs">
        <IconFilter size={24} />
        <Title order={3}>Member Funnel — where people drop off</Title>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        Each step is a group of members. Click a row to open the exact people
        inside it — with phone numbers and a WhatsApp button.
      </Text>

      {/* Legend so the two row types are never confused */}
      <Group gap="lg" mb="lg">
        <Group gap={6}>
          <ThemeIcon color="teal" variant="light" size="sm" radius="xl">
            <IconUserCheck size={14} />
          </ThemeIcon>
          <Text size="xs" c="dimmed">
            <b>Solid bar</b> = members who reached this step
          </Text>
        </Group>
        <Group gap={6}>
          <ThemeIcon color="red" variant="light" size="sm" radius="xl">
            <IconUserX size={14} />
          </ThemeIcon>
          <Text size="xs" c="dimmed">
            <b>Red card</b> = members who stalled and never continued
          </Text>
        </Group>
      </Group>

      {isLoading || !d ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <>
          <Stack gap="sm">
            {d.funnel.map((s, i) => {
              const dropKey = DROP_KEY[i] || s.key;
              const showDrop = i > 0 && s.dropFromPrev > 0;
              return (
                <div key={s.key}>
                  {/* Drop-off group — a DISTINCT red card, not part of the bar */}
                  {showDrop && (
                    <>
                      <Card
                        withBorder
                        radius="md"
                        padding="sm"
                        mb={4}
                        onClick={() => toggle(dropKey)}
                        style={{
                          cursor: "pointer",
                          borderColor: "var(--mantine-color-red-4)",
                          borderStyle: "dashed",
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap={8} wrap="nowrap">
                            <ThemeIcon color="red" variant="light" size="md">
                              <IconUserX size={16} />
                            </ThemeIcon>
                            <div>
                              <Text size="sm" fw={600} c="red.7">
                                {s.dropFromPrev.toLocaleString()} members —{" "}
                                {DROP_TITLE[dropKey] || "dropped off"}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {DROP_DESC[dropKey]}
                              </Text>
                            </div>
                          </Group>
                          <Group gap={6} wrap="nowrap">
                            <Badge color="red" variant="light">
                              {100 - s.pctOfPrev}% lost here
                            </Badge>
                            {panels[dropKey] ? (
                              <IconChevronDown size={16} />
                            ) : (
                              <IconChevronRight size={16} />
                            )}
                          </Group>
                        </Group>
                      </Card>
                      {panels[dropKey] && (
                        <CohortInline
                          state={panels[dropKey]}
                          title={`${DROP_TITLE[dropKey]} — ${
                            panels[dropKey].data?.total ?? ""
                          } members`}
                        />
                      )}
                    </>
                  )}

                  {/* Survivor stage — the solid bar */}
                  <Card
                    withBorder
                    padding="md"
                    radius="md"
                    onClick={() => toggle(s.key)}
                    style={{ cursor: "pointer" }}
                  >
                    <Group justify="space-between" mb={6} wrap="nowrap">
                      <Group gap={8} wrap="nowrap">
                        <ThemeIcon
                          color={i === 0 ? "blue" : "teal"}
                          variant="light"
                          size="md"
                        >
                          <IconUserCheck size={16} />
                        </ThemeIcon>
                        <div>
                          <Group gap={6}>
                            <Text fw={700}>
                              Step {i + 1}: {s.label}
                            </Text>
                            {panels[s.key] ? (
                              <IconChevronDown size={15} />
                            ) : (
                              <IconChevronRight size={15} />
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {STAGE_DESC[s.key]}
                          </Text>
                        </div>
                      </Group>
                      <Group gap="xs" wrap="nowrap">
                        <Text fw={800} size="lg">
                          {s.count.toLocaleString()}
                        </Text>
                        <Badge variant="light" color="gray">
                          {s.pctOfTotal}% of all
                        </Badge>
                      </Group>
                    </Group>
                    <Progress
                      value={s.pctOfTotal}
                      size="lg"
                      radius="sm"
                      color={
                        i === 0 ? "blue" : s.pctOfPrev < 60 ? "orange" : "teal"
                      }
                    />
                  </Card>
                  {panels[s.key] && (
                    <CohortInline
                      state={panels[s.key]}
                      title={`Step ${i + 1}: ${s.label} — ${
                        panels[s.key].data?.total ?? ""
                      } members`}
                    />
                  )}
                </div>
              );
            })}
          </Stack>

          {d.notMonetized && (
            <>
              <Title order={5} mt="xl" mb={4}>
                Where the not-monetized are stuck{" "}
                <Text span c="dimmed" size="xs">
                  ({d.notMonetized.total.toLocaleString()} members · furthest step
                  reached)
                </Text>
              </Title>
              <Text c="dimmed" size="xs" mb="sm">
                The gate to earn = complete profile + 5 tutorials + 1 linked
                social. These are the live members who haven't crossed it yet.
              </Text>
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                {[
                  {
                    label: "Signed up, never opened",
                    value: d.notMonetized.stages.neverOpened,
                    hint: "created an account, never came back in",
                  },
                  {
                    label: "Opened, 0 tutorials",
                    value: d.notMonetized.stages.openedNoTutorial,
                    hint: "in the app but never started — the main leak",
                  },
                  {
                    label: "Mid-tutorial (1–4)",
                    value: d.notMonetized.stages.midTutorial,
                    hint: "started, didn't finish the 5",
                  },
                  {
                    label: "Done tutorials, needs social/profile",
                    value: d.notMonetized.stages.finishedNeedsSocialOrProfile,
                    hint: "one step from earning",
                  },
                ].map((s) => (
                  <Card key={s.label} withBorder padding="md" radius="md">
                    <Text size="1.6rem" fw={800}>
                      {s.value.toLocaleString()}
                    </Text>
                    <Text size="xs" fw={600} mt={2}>
                      {s.label}
                    </Text>
                    <Text size="xs" c="dimmed" mt={2}>
                      {s.hint}
                    </Text>
                  </Card>
                ))}
              </SimpleGrid>
              <Group gap="lg" mt="sm" mb="xs">
                <Text size="xs" c="dimmed">
                  <b>Still need:</b> profile {d.notMonetized.gates.needProfile} ·
                  tutorials {d.notMonetized.gates.needTutorials} · social{" "}
                  {d.notMonetized.gates.needSocial}
                </Text>
                <Text size="xs" c="dimmed">
                  <b>Warmth:</b> opened this week{" "}
                  {d.notMonetized.warmth.openedThisWeek} · 7–30d{" "}
                  {d.notMonetized.warmth.opened7to30d} · never opened{" "}
                  {d.notMonetized.warmth.neverOpened}
                </Text>
              </Group>
            </>
          )}

          <Title order={5} mt="xl" mb="sm">
            Setup milestones{" "}
            <Text span c="dimmed" size="xs">
              (click to see who's missing it)
            </Text>
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            {[
              {
                label: "Verified phone",
                value: d.setup.verifiedPhone,
                miss: "no_phone",
              },
              {
                label: "Set transaction PIN",
                value: d.setup.setPin,
                miss: "no_pin",
              },
              { label: "Added bank", value: d.setup.addedBank, miss: "no_bank" },
            ].map((m) => (
              <Card
                key={m.miss}
                withBorder
                padding="md"
                radius="md"
                onClick={() => toggle(m.miss)}
                style={{ cursor: "pointer" }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    {m.label}
                  </Text>
                  {panels[m.miss] ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronRight size={14} />
                  )}
                </Group>
                <Text size="1.4rem" fw={800}>
                  {m.value.toLocaleString()}
                </Text>
                <Text size="xs" c="dimmed">
                  {panels[m.miss]
                    ? "hide who's missing it"
                    : "click: who's missing it"}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
          {/* Setup cohort tables render full-width below the grid */}
          {["no_phone", "no_pin", "no_bank"]
            .filter((k) => panels[k])
            .map((k) => (
              <Box key={k} mt="sm">
                <CohortInline
                  state={panels[k]}
                  title={`${SETUP_TITLE[k]} — ${panels[k].data?.total ?? ""} members`}
                />
              </Box>
            ))}

          <Group mt="lg" justify="flex-end">
            <Badge variant="light" color="gray">
              as of {new Date(d.generatedAt).toLocaleString()}
            </Badge>
          </Group>
        </>
      )}
    </Box>
  );
}

// The list of actual members for a cohort, rendered INLINE beneath its row,
// headed with a bold statement of exactly who is in the list.
function CohortInline({
  state,
  title,
}: {
  state: PanelState;
  title: string;
}) {
  if (state.loading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }
  const cohort = state.data;
  if (!cohort) return null;

  return (
    <Card withBorder radius="md" padding="sm" mt={4} bg="var(--bg-surface-2)">
      <Group justify="space-between" align="flex-start" mb={2} wrap="nowrap">
        <Text fw={700} size="sm">
          👥 {title}
        </Text>
        {/* Bulk phone-number export (CSV download + copy-all) removed —
            member data is projected, not downloadable (data-protection / legal).
            Per-member WhatsApp buttons below remain for one-by-one follow-up. */}
      </Group>
      <Text size="xs" c="dimmed" mb="xs">
        Showing {cohort.shown ?? 0} of {(cohort.total ?? 0).toLocaleString()}.
        WhatsApp opens a chat to that number (nothing is sent until you type a
        message).
      </Text>
      {(cohort.users ?? []).length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No members in this group.
        </Text>
      ) : (
        <ScrollArea.Autosize mah={420}>
          <Table striped highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Member</Table.Th>
                <Table.Th>Phone</Table.Th>
                <Table.Th>Joined</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Message</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(cohort.users ?? []).map((u) => {
                const wa = waLink(u.phone);
                return (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <Text fw={600} size="sm">
                        {u.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {u.email}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {u.phone ? (
                        <Group gap={4}>
                          <IconPhone size={13} />
                          <Text size="sm">{u.phone}</Text>
                        </Group>
                      ) : (
                        <Text size="xs" c="dimmed">
                          no phone
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ago(u.joinedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {u.monetized && (
                          <Badge size="xs" color="teal">
                            monetized
                          </Badge>
                        )}
                        {u.balance > 0 && (
                          <Badge size="xs" variant="light">
                            ₦{u.balance.toLocaleString()}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {wa ? (
                        <Button
                          size="xs"
                          color="green"
                          variant="light"
                          leftSection={<IconBrandWhatsapp size={14} />}
                          component="a"
                          href={wa}
                          target="_blank"
                          rel="noreferrer"
                        >
                          WhatsApp
                        </Button>
                      ) : (
                        <Tooltip label="No usable phone number">
                          <Button size="xs" variant="light" disabled>
                            WhatsApp
                          </Button>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      )}
    </Card>
  );
}
